import { useQuery } from '@tanstack/react-query'
import { Check, FileUp } from 'lucide-react'
import { useRef, useState } from 'react'
import { useParams } from 'react-router'
import { Button } from '@/components/Button'
import { LanguageSwitch } from '@/components/LanguageSwitch'
import { Logo } from '@/components/Logo'
import { useI18n } from '@/i18n/i18n'
import { formatBytes, MAX_UPLOAD } from '@/board/upload'

// Where a person hands a file to an outside AI app that cannot send it itself
// (worker/uploads.ts): the app gives them this link from upload_url. The link
// is the only access, so it works signed out (on a phone too), and it can do
// one thing: deliver one file to that board for that app to place.

type Info = {
  board: string
  client: string | null
  expiresAt: number
  received: { name: string; size: number } | null
  placed: boolean
}

type State =
  | { phase: 'idle'; error?: string }
  | { phase: 'sending'; name: string; done: number; total: number }
  | { phase: 'done'; name: string }

/** PUT with upload progress, which fetch does not report. */
function send(url: string, file: File, onProgress: (done: number) => void): Promise<number> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', `${url}?name=${encodeURIComponent(file.name)}`)
    if (file.type) xhr.setRequestHeader('content-type', file.type)
    xhr.upload.onprogress = (e) => {
      onProgress(e.loaded)
    }
    xhr.onload = () => {
      resolve(xhr.status)
    }
    xhr.onerror = () => {
      reject(new Error('network'))
    }
    xhr.send(file)
  })
}

export function Handoff() {
  const { t } = useI18n()
  const { token = '' } = useParams()
  const url = `/api/uploads/${encodeURIComponent(token)}`
  const info = useQuery({
    queryKey: ['handoff', token],
    queryFn: async () => {
      const res = await fetch(url)
      if (!res.ok) return null
      const i = (await res.json()) as Info
      return { ...i, minutes: Math.max(1, Math.round((i.expiresAt - Date.now()) / 60_000)) }
    },
    retry: false,
  })
  const [state, setState] = useState<State>({ phase: 'idle' })
  const [over, setOver] = useState(false)
  const picker = useRef<HTMLInputElement>(null)

  const deliver = async (file: File | undefined) => {
    if (!file || state.phase === 'sending') return
    if (file.size > MAX_UPLOAD) {
      setState({ phase: 'idle', error: t('upload.tooLarge') })
      return
    }
    setState({ phase: 'sending', name: file.name, done: 0, total: file.size })
    try {
      const status = await send(url, file, (done) => {
        setState({ phase: 'sending', name: file.name, done, total: file.size })
      })
      if (status === 404 || status === 409) {
        void info.refetch()
        setState({ phase: 'idle' })
        return
      }
      setState(
        status < 300
          ? { phase: 'done', name: file.name }
          : { phase: 'idle', error: status === 413 ? t('upload.tooLarge') : t('handoff.failed') },
      )
    } catch {
      setState({ phase: 'idle', error: t('handoff.failed') })
    }
  }

  const data = info.data
  const client = data?.client
  const received = state.phase === 'done' ? state.name : data?.received?.name
  const minutes = data?.minutes ?? 0

  let body
  if (info.isPending) body = null
  else if (!data && !received)
    body = (
      <>
        <h1 className="text-[22px] font-semibold tracking-[-0.02em]">{t('handoff.gone')}</h1>
        <p className="mt-2 text-[14px] leading-relaxed text-muted">{t('handoff.goneBody')}</p>
      </>
    )
  else if (received)
    body = (
      <div className="flex gap-3">
        <span className="mt-1 grid size-7 flex-none place-items-center rounded-full bg-ai-soft text-ai">
          <Check size={16} strokeWidth={2.5} />
        </span>
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.02em] break-all">
            {t('handoff.done', { name: received })}
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-ink-2">
            {data?.placed
              ? client
                ? t('handoff.placed', { client, board: data.board })
                : t('handoff.placedNoClient', { board: data.board })
              : client
                ? t('handoff.doneNext', { client })
                : t('handoff.doneNextNoClient')}
          </p>
        </div>
      </div>
    )
  else
    body = (
      <>
        <h1 className="text-[22px] font-semibold tracking-[-0.02em]">
          {client ? t('handoff.title', { client }) : t('handoff.titleNoClient')}
        </h1>
        <p className="mt-2 text-[14px] leading-relaxed text-muted">
          {t('handoff.where', { board: data?.board ?? '', minutes })}
        </p>
        <div
          onDragOver={(e) => {
            e.preventDefault()
            setOver(true)
          }}
          onDragLeave={() => {
            setOver(false)
          }}
          onDrop={(e) => {
            e.preventDefault()
            setOver(false)
            void deliver(e.dataTransfer.files[0])
          }}
          className={`mt-6 flex flex-col items-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
            over ? 'border-ai bg-ai-soft' : 'border-line-strong bg-surface'
          }`}
        >
          {state.phase === 'sending' ? (
            <div className="w-full max-w-xs" role="status">
              <div className="truncate text-[13.5px] text-ink-2">
                {t('handoff.sending', { name: state.name })}
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface-2">
                <div
                  className="h-full rounded-full bg-ai transition-[width]"
                  style={{ width: `${Math.round((state.done / Math.max(1, state.total)) * 100)}%` }}
                />
              </div>
              <div className="mt-2 text-[12px] text-muted tabular-nums">
                {formatBytes(state.done)} / {formatBytes(state.total)}
              </div>
            </div>
          ) : (
            <>
              <FileUp size={28} strokeWidth={1.5} className="text-muted" aria-hidden="true" />
              <div className="mt-3 text-[15px] font-medium">{t('handoff.drop')}</div>
              <div className="my-2 text-[12.5px] text-muted">{t('handoff.or')}</div>
              <Button
                variant="primary"
                onClick={() => {
                  picker.current?.click()
                }}
              >
                {t('handoff.pick')}
              </Button>
              <input
                ref={picker}
                type="file"
                className="hidden"
                onChange={(e) => {
                  void deliver(e.target.files?.[0])
                  e.target.value = ''
                }}
              />
            </>
          )}
        </div>
        {state.phase === 'idle' && state.error && (
          <p className="mt-3 text-[13px] text-danger" role="alert">
            {state.error}
          </p>
        )}
        <p className="mt-3 text-[12.5px] leading-relaxed text-muted">{t('handoff.limit')}</p>
      </>
    )

  return (
    <div className="flex min-h-full flex-col">
      <header className="mx-auto flex h-14 w-full max-w-xl items-center justify-between px-4 sm:px-6">
        <Logo />
        <LanguageSwitch />
      </header>
      <main className="mx-auto w-full max-w-xl flex-1 px-4 pt-8 pb-16 sm:px-6 sm:pt-14">
        {body}
      </main>
    </div>
  )
}
